import type { ScopedConnectionItem } from '@/workspace/connection-api';
import { formatCopy } from '@/i18n/ns';
import type { WelcomeCopyKey } from '@/i18n/ns/welcome';
import type { Focus } from './preferences';

// A tool's copy is keys into the 24-language `welcome` namespace: the summary
// sits on its connector card and the prompt becomes the person's first
// message, so both follow the interface language.
export interface OnboardingTool {
  readonly focuses: readonly Focus[];
  readonly summary: WelcomeCopyKey;
  readonly title: WelcomeCopyKey;
  readonly prompt: WelcomeCopyKey;
  readonly pipedreamApps?: readonly string[];
}

export type OnboardingToolRegistry = Readonly<Record<string, OnboardingTool>>;
/** The `welcome` copy the selectors read; a fixture registry brings its own. */
export type ToolCopy = Readonly<Record<string, string>>;

// Presentation-only metadata. Connection status, audience, permissions and
// provider descriptions remain owned by the server's connection catalogue.
export const onboardingTools = {
  'google-workspace': {
    pipedreamApps: ['gmail', 'google_calendar', 'google_drive', 'google_sheets'],
    focuses: ['everyday'],
    summary: 'toolGoogleSummary',
    title: 'taskMeetingTitle',
    prompt: 'taskMeetingPrompt',
  },
  slack: {
    pipedreamApps: ['slack_v2'],
    focuses: ['everyday', 'projects'],
    summary: 'toolSlackSummary',
    title: 'taskSlackTitle',
    prompt: 'taskSlackPrompt',
  },
  shopify: {
    // A store connects through its own Shopify Developer App, which the catalogue lists itself.
    pipedreamApps: [],
    focuses: ['commerce'],
    summary: 'toolShopifySummary',
    title: 'taskShopifyTitle',
    prompt: 'taskShopifyPrompt',
  },
  woocommerce: {
    focuses: ['commerce'],
    summary: 'toolWooSummary',
    title: 'taskWooTitle',
    prompt: 'taskWooPrompt',
  },
  'google-merchant-center': {
    pipedreamApps: ['google_merchant_center'],
    focuses: ['commerce'],
    summary: 'toolMerchantSummary',
    title: 'taskFeedTitle',
    prompt: 'taskFeedPrompt',
  },
  fortnox: {
    focuses: ['finance'],
    summary: 'toolInvoicesSummary',
    title: 'taskFortnoxTitle',
    prompt: 'taskFortnoxPrompt',
  },
  tripletex: {
    focuses: ['finance'],
    summary: 'toolInvoicesSummary',
    title: 'taskTripletexTitle',
    prompt: 'taskTripletexPrompt',
  },
  github: {
    focuses: ['projects'],
    summary: 'toolGithubSummary',
    title: 'taskGithubTitle',
    prompt: 'taskGithubPrompt',
  },
} as const satisfies OnboardingToolRegistry;

export function pipedreamAppsForFocus(
  focuses: readonly Focus[],
  registry: OnboardingToolRegistry = onboardingTools,
) {
  return toolIdsForFocus(focuses, registry).flatMap((id) => registry[id].pipedreamApps ?? [id]);
}

export function toolIdsForFocus(
  focus: Focus | readonly Focus[],
  registry: OnboardingToolRegistry = onboardingTools,
): string[] {
  return Object.entries(registry)
    .filter(([, tool]) =>
      tool.focuses.some((entry) => (Array.isArray(focus) ? focus : [focus]).includes(entry)),
    )
    .map(([id]) => id);
}

export function toolSummary(
  id: string,
  copy: ToolCopy,
  registry: OnboardingToolRegistry = onboardingTools,
): string | undefined {
  const tool = Object.hasOwn(registry, id) ? registry[id] : undefined;
  return tool ? copy[tool.summary] : undefined;
}

export function firstTasks(
  items: ReadonlyArray<Pick<ScopedConnectionItem, 'id' | 'status'> & { name?: string }>,
  copy: ToolCopy,
  registry: OnboardingToolRegistry = onboardingTools,
) {
  // Only server-reported connected accounts receive a provider-specific task.
  const ids = [
    ...new Set(items.filter((item) => item.status === 'connected').map((item) => item.id)),
  ];
  return ids
    .flatMap((id) => {
      const providerId = id.startsWith('pipedream:') ? id.slice(10) : id;
      const tool = Object.hasOwn(registry, providerId) ? registry[providerId] : undefined;
      if (tool) return [{ id: providerId, title: copy[tool.title], prompt: copy[tool.prompt] }];
      const item = items.find((entry) => entry.id === id);
      return id.startsWith('pipedream:') && item?.name
        ? [
            {
              id: providerId,
              title: formatCopy(copy.taskTryTitle, { name: item.name }),
              prompt: formatCopy(copy.taskTryPrompt, { name: item.name }),
            },
          ]
        : [];
    })
    .slice(0, 3);
}

// Starter tasks are offered with or without connections. Their titles and
// prompts are keys into the 24-language `welcome` namespace: the prompt becomes
// the person's first message, so it follows the interface language.
export const starterTasks = [
  { id: 'research', title: 'taskResearchTitle', prompt: 'taskResearchPrompt' },
  { id: 'file', title: 'taskFileTitle', prompt: 'taskFilePrompt' },
  { id: 'first-task', title: 'taskChooseTitle', prompt: 'taskChoosePrompt' },
] as const satisfies readonly {
  readonly id: string;
  readonly title: WelcomeCopyKey;
  readonly prompt: WelcomeCopyKey;
}[];
export type StarterTaskId = (typeof starterTasks)[number]['id'];

/** With connections, the document task gives way to the connected tools' tasks. */
export function starterTasksFor(connectedCount: number) {
  return starterTasks.filter((task) => connectedCount === 0 || task.id !== 'file');
}
